import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  listFlights,
  listAssetsByFlight,
  deleteAsset,
  deleteFlight,
} from "../offline/store";
import { isPersistedStorage, requestPersistedStorage } from "../offline/persist";
import { getStorageEstimate } from "../offline/quota";
import { auditFlight, type AuditResult, type AuditStatus } from "../offline/audit";
import type { OfflineFlightRecord } from "../offline/schema";
import { useTourAutoStart } from "../tour/useTourAutoStart";
import { OfflineBasemapNote } from "../components/OfflineBasemapNote";

const HEALTH_ICON: Record<AuditStatus, string> = {
  ok: "🟢",
  evicted: "🟡",
  "schema-mismatch": "🔴",
  "not-cached": "🔴",
  partial: "🟠",
};

const HEALTH_LABEL: Record<AuditStatus, string> = {
  ok: "OK",
  evicted: "Some assets evicted",
  "schema-mismatch": "Schema mismatch — re-download required",
  "not-cached": "Not cached",
  partial: "Partial download — remove and download again",
};

export interface OfflinePageProps {
  /** Wired by App.tsx — refreshes the per-flight cache by comparing meta and
   *  re-downloading if anything material changed. Omitted in standalone tests. */
  onRefresh?: (flightId: string) => Promise<void>;
}

export function OfflinePage({ onRefresh }: OfflinePageProps = {}): JSX.Element {
  const [flights, setFlights] = useState<OfflineFlightRecord[]>([]);
  const [persisted, setPersisted] = useState(false);
  const [estimate, setEstimate] = useState({ quota: 0, usage: 0 });
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [audits, setAudits] = useState<Record<string, AuditResult>>({});
  const [verifying, setVerifying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [searchParams] = useSearchParams();
  // Carry the admin's `?team=` through to the viewer so a cross-team row opens
  // the flight it actually names.
  const teamParam = searchParams.get("team");
  const teamQs = teamParam !== null && teamParam !== ""
    ? `?team=${encodeURIComponent(teamParam)}`
    : "";

  async function refresh(): Promise<void> {
    setFlights(await listFlights());
    setPersisted(await isPersistedStorage());
    setEstimate(await getStorageEstimate());
    setLoaded(true);
  }

  useEffect(() => {
    void refresh();
  }, []);
  useTourAutoStart("offline", loaded);

  async function handleRemove(flightId: string): Promise<void> {
    if (!window.confirm(`Remove cached flight ${flightId}?`)) return;
    const assets = await listAssetsByFlight(flightId);
    for (const a of assets) await deleteAsset(a.key);
    await deleteFlight(flightId);
    await refresh();
  }

  async function handleRefresh(flightId: string): Promise<void> {
    if (onRefresh === undefined) return;
    setRefreshingId(flightId);
    try {
      await onRefresh(flightId);
    } finally {
      setRefreshingId(null);
      await refresh();
    }
  }

  async function handleGrantPersistent(): Promise<void> {
    await requestPersistedStorage();
    await refresh();
  }

  async function handleVerifyAll(): Promise<void> {
    setVerifying(true);
    try {
      const out: Record<string, AuditResult> = {};
      for (const f of flights) {
        out[f.flightId] = await auditFlight(f.flightId);
      }
      setAudits(out);
    } finally {
      setVerifying(false);
    }
  }

  const usedMb = Math.round(estimate.usage / 1024 / 1024);
  return (
    <div className="vmc-offline-page">
      <h2>Offline flights</h2>
      <div className="vmc-offline-summary" data-tour="offline.summary">
        <span>{flights.length} cached</span>
        <span>{usedMb} MB used</span>
        <span>
          Persistent storage:{" "}
          {persisted ? (
            "granted"
          ) : (
            <button type="button" onClick={() => void handleGrantPersistent()}>
              Grant persistent storage
            </button>
          )}
        </span>
        {flights.length > 0 && (
          <button
            type="button"
            data-tour="offline.verify"
            disabled={verifying}
            onClick={() => void handleVerifyAll()}
          >
            {verifying ? "Verifying…" : "Verify all"}
          </button>
        )}
      </div>
      <OfflineBasemapNote />
      {flights.length === 0 ? (
        <p>No flights are cached for offline use yet.</p>
      ) : (
        <table className="vmc-offline-table" data-tour="offline.table">
          <thead>
            <tr>
              <th>Flight</th>
              <th>Quality</th>
              <th>Size</th>
              <th>Cached</th>
              <th>Health</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {flights.map((f) => {
              const audit = audits[f.flightId];
              const missingSummary =
                audit && audit.missing.length > 0
                  ? ` — missing: ${audit.missing.join(", ")}`
                  : "";
              // A partial record is self-describing: a cancelled or failed
              // download strands its assets, so flag it without making the
              // operator run Verify all to find the wasted gigabytes.
              const status: AuditStatus | undefined =
                f.partial === true ? "partial" : audit?.status;
              return (
              <tr key={f.flightId}>
                <td>
                  <Link to={`/flights/${encodeURIComponent(f.flightId)}${teamQs}`}>
                    {f.meta?.name ?? f.flightId}
                  </Link>
                </td>
                <td>{f.videoQuality}</td>
                <td>{Math.round(f.byteSizeApprox / 1024 / 1024)} MB</td>
                <td>{new Date(f.cachedAt).toLocaleString()}</td>
                <td>
                  {status !== undefined ? (
                    <span
                      title={`${HEALTH_LABEL[status]}${missingSummary}`}
                      aria-label={HEALTH_LABEL[status]}
                    >
                      {HEALTH_ICON[status]}
                    </span>
                  ) : (
                    <span aria-label="Not yet verified">—</span>
                  )}
                </td>
                <td data-tour="offline.actions">
                  {onRefresh !== undefined && (
                    <button
                      type="button"
                      disabled={refreshingId !== null}
                      onClick={() => void handleRefresh(f.flightId)}
                    >
                      {refreshingId === f.flightId ? "Refreshing…" : "Refresh"}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={refreshingId !== null}
                    onClick={() => void handleRemove(f.flightId)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
